import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import { csvFileDownload } from "../../../../helpers/files";
import TableLayout from "../../../../layout/TableLayout/TableLayout";
import { BulkUploadStatus } from "../../../../pages/NewTG/Datasets/store/tg-dataset-store.tsx";
import styles from "./AdvancedImageLoadingCompleted.module.scss";

type Props = {
	status: BulkUploadStatus;
	handleContinueClick?: () => void;
	onDownloadLog?: () => void;
	isFromCreateDataset?: boolean;
};

const AdvancedImageLoadingCompleted = ({
	status,
	handleContinueClick,
	onDownloadLog,
	isFromCreateDataset = false,
}: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "advancedUploadPanels.advancedUpload.completed" });

	const failedRows = status.failed_images ?? [];

	const tableData = useMemo(
		() =>
			failedRows.map((row, idx) => ({
				key: `${idx}`,
				data: [row.filename, t("failed"), row.error_code ?? "", row.error_message ?? ""],
			})),
		[failedRows, t],
	);

	const handleDownload = () => {
		const header = [t("fileName"), t("status"), t("errorCode"), t("errorMessage")];
		const rows = failedRows.map((r) => [r.filename, "failed", r.error_code ?? "", r.error_message ?? ""]);
		const csv = [header, ...rows]
			.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
			.join("\n");
		csvFileDownload(csv, "failed_images_log.csv");
	};

	const headerArray = [t("table.fileName"), t("table.status"), t("table.errorCode"), t("table.errorMessage")];

	const getFailedStatusRow = (row: any) => {
		return (
			<Box key={`status-${row.key}`} className={styles.failedPill}>
				<Typography className={styles.failedPillText}>{t("failed")}</Typography>
			</Box>
		);
	};

	return (
		<Box className={styles.container}>
			<Typography className={styles.title}>{t("pageTitle")}</Typography>
			<Box className={styles.summaryCards}>
				<Box className={styles.card}>
					<Typography className={styles.cardTitle}>{t("totalImagesProcessed")}</Typography>
					<Typography
						className={styles.cardValue}
					>{`${status.total_processed}/${status.total_processed}`}</Typography>
				</Box>
				<Box className={`${styles.card} ${styles.successCard}`}>
					<Typography className={styles.cardTitle}>{t("successfullyUploaded")}</Typography>
					<Typography className={styles.cardValue}>{`${status.done}/${status.total_processed}`}</Typography>
				</Box>
				<Box className={`${styles.card} ${styles.failedCard}`}>
					<Typography className={styles.cardTitle}>{t("failedUploaded")}</Typography>
					<Typography className={styles.cardValue}>{`${status.failed}/${status.total_processed}`}</Typography>
				</Box>
			</Box>

			{failedRows.length > 0 ? (
				<Box className={styles.tableWrapper}>
					<TableLayout
						title={t("logTitle")}
						description={t("logDescription")}
						stickyHeader
						tableData={tableData.map((r) => ({
							...r,
							data: [r.data[0], getFailedStatusRow(r), r.data[2], r.data[3]],
						}))}
						headerArray={headerArray}
						headerComponents={{
							"3": (
								<Box className={styles.downloadHeader}>
									<Typography component="span">{t("table.errorMessage")}</Typography>
									<BriaButton buttonType="secondarySmall" onClick={onDownloadLog ?? handleDownload}>
										{t("downloadLog")}
									</BriaButton>
								</Box>
							),
						}}
						hideHeader={true}
					/>
				</Box>
			) : null}

			<Box className={styles.actions}>
				<BriaButton
					buttonType="primaryMedium"
					onClick={handleContinueClick}
					className={styles.startTrainingButton}
				>
					{isFromCreateDataset ? t("goToDatasetsOverview") : t("startTraining")}
				</BriaButton>
			</Box>
		</Box>
	);
};

export default observer(AdvancedImageLoadingCompleted);
